/** Path command ids, matching CanvasKit's *_VERB constants (checked at load in lib/ck.ts). */
export const VERB = { MOVE: 0, LINE: 1, QUAD: 2, CONIC: 3, CUBIC: 4, CLOSE: 5 } as const;

export type Pt = { x: number; y: number };

export type Segment =
  | { kind: "line"; p0: Pt; p1: Pt }
  | { kind: "quad"; p0: Pt; c: Pt; p1: Pt }
  | { kind: "conic"; p0: Pt; c: Pt; p1: Pt; w: number }
  | { kind: "cubic"; p0: Pt; c0: Pt; c1: Pt; p1: Pt };

export interface Contour {
  segments: Segment[];
  closed: boolean;
  start: Pt;
}

/** Parse CanvasKit Path.toCmds() output. */
export function parseCmds(cmds: ArrayLike<number>): Contour[] {
  const out: Contour[] = [];
  let cur: Contour | null = null;
  let last: Pt = { x: 0, y: 0 };
  let i = 0;
  const p = (): Pt => ({ x: cmds[i++], y: cmds[i++] });
  while (i < cmds.length) {
    const verb = cmds[i++];
    switch (verb) {
      case VERB.MOVE:
        last = p();
        cur = { segments: [], closed: false, start: last };
        out.push(cur);
        break;
      case VERB.LINE: {
        const p1 = p();
        cur!.segments.push({ kind: "line", p0: last, p1 });
        last = p1;
        break;
      }
      case VERB.QUAD: {
        const c = p();
        const p1 = p();
        cur!.segments.push({ kind: "quad", p0: last, c, p1 });
        last = p1;
        break;
      }
      case VERB.CONIC: {
        const c = p();
        const p1 = p();
        const w = cmds[i++];
        cur!.segments.push({ kind: "conic", p0: last, c, p1, w });
        last = p1;
        break;
      }
      case VERB.CUBIC: {
        const c0 = p();
        const c1 = p();
        const p1 = p();
        cur!.segments.push({ kind: "cubic", p0: last, c0, c1, p1 });
        last = p1;
        break;
      }
      case VERB.CLOSE:
        if (cur) {
          // An implicit closing edge is a real segment once the path is warped (it may curve).
          if (last.x !== cur.start.x || last.y !== cur.start.y) {
            cur.segments.push({ kind: "line", p0: last, p1: cur.start });
          }
          cur.closed = true;
          last = cur.start;
        }
        break;
      default:
        throw new Error(`Unknown path verb ${verb}`);
    }
  }
  return out;
}

export function segmentPoint(s: Segment, t: number): Pt {
  const mt = 1 - t;
  switch (s.kind) {
    case "line":
      return { x: s.p0.x * mt + s.p1.x * t, y: s.p0.y * mt + s.p1.y * t };
    case "quad": {
      const a = mt * mt, b = 2 * mt * t, c = t * t;
      return { x: a * s.p0.x + b * s.c.x + c * s.p1.x, y: a * s.p0.y + b * s.c.y + c * s.p1.y };
    }
    case "conic": {
      const a = mt * mt, b = 2 * mt * t * s.w, c = t * t;
      const d = a + b + c;
      return { x: (a * s.p0.x + b * s.c.x + c * s.p1.x) / d, y: (a * s.p0.y + b * s.c.y + c * s.p1.y) / d };
    }
    case "cubic": {
      const a = mt * mt * mt, b = 3 * mt * mt * t, c = 3 * mt * t * t, d = t * t * t;
      return {
        x: a * s.p0.x + b * s.c0.x + c * s.c1.x + d * s.p1.x,
        y: a * s.p0.y + b * s.c0.y + c * s.c1.y + d * s.p1.y,
      };
    }
  }
}

/**
 * Tangent direction at t. Uses a small finite difference so degenerate control points
 * (c0 == p0 etc.) still give a usable direction.
 */
export function segmentTangent(s: Segment, t: number): Pt {
  const h = 1e-4;
  const a = segmentPoint(s, Math.max(0, t - h));
  const b = segmentPoint(s, Math.min(1, t + h));
  return { x: b.x - a.x, y: b.y - a.y };
}
